import React from 'react';
import { attributionLinkService } from '../../services/attributionLinkService';
import Modal from '../ui/Modal';
import Tabs from '../ui/Tabs';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import EventBus from '../../utils/EventBus';
import styles from './ShortLinkField.module.css';

// Same public short-link domain AttributionLinks.jsx uses — shown for
// display only (the tagged link's own URL), never written into the message
// body/templateParams here (see the class comment below on why).
export const REDIRECT_BASE_URL = 'https://dl.pickmywork.com/';

// WhatsApp/SMS/RCS give no click signal from the provider at all. This does
// NOT insert a link into the message — WhatsApp templates are pre-approved
// by Meta and DLT SMS templates by the telecom operator, both externally,
// before they're even selectable here; the actual link a recipient taps is
// already baked into that approved template's content (the marketer put it
// there when registering the template with Meta/DLT, using one of our own
// short links). This field only TAGS which short link that already is, so
// its real clicks can be rolled up as this exact message's "link clicks"
// (see journeys.controller.js/campaignContent.model.js for how the tag is
// saved) — it never touches `text`/`templateParams` itself.
//
// attributionLinkId is a sibling prop, stored as its own field, never inside
// the message template (that field rides straight into the provider's
// send() payload — see campaign.validator.js's own comment on this).
class ShortLinkField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      linkDetails: null, loadingDetails: false,
      showModal: false, modalTab: 'existing',
      existingLinks: [], existingLoading: false,
      newName: '', newUrl: '', creating: false, formError: null,
    };
    this._loadDetails = this._loadDetails.bind(this);
  }

  componentDidMount() {
    this._loadDetails();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.attributionLinkId !== this.props.attributionLinkId) this._loadDetails();
  }

  _loadDetails() {
    const { attributionLinkId } = this.props;
    if (!attributionLinkId) return this.setState({ linkDetails: null });
    if (this.state.linkDetails?.id === attributionLinkId) return;

    this.setState({ loadingDetails: true });
    attributionLinkService.getById(attributionLinkId)
      .then((link) => this.setState({ loadingDetails: false, linkDetails: link }))
      .catch(() => this.setState({ loadingDetails: false, linkDetails: null }));
  }

  openModal() {
    this.setState({ showModal: true, modalTab: 'existing', newName: '', newUrl: '', formError: null, existingLoading: true });
    attributionLinkService.getAll({ destination_type: 'external', untagged: true, limit: 50 })
      .then(({ data }) => this.setState({ existingLoading: false, existingLinks: data }))
      .catch(() => this.setState({ existingLoading: false }));
  }

  selectExisting(link) {
    this.setState({ showModal: false });
    this.props.onTag(link);
  }

  createAndTag() {
    const { newName, newUrl } = this.state;
    if (!newName.trim()) return this.setState({ formError: 'Name is required.' });
    if (!/^https?:\/\//i.test(newUrl)) return this.setState({ formError: 'Destination URL must start with http:// or https://.' });

    this.setState({ creating: true, formError: null });
    attributionLinkService.create({ name: newName, channel_type: 'other', destination_type: 'external', external_url: newUrl })
      .then((link) => {
        this.setState({ creating: false, showModal: false });
        this.props.onTag(link);
      })
      .catch((err) => {
        this.setState({ creating: false, formError: err.message || 'Failed to create link.' });
      });
  }

  remove() {
    this.setState({ linkDetails: null });
    this.props.onUntag();
    EventBus.emit('toast', { type: 'success', message: 'Short link untagged from this message.' });
  }

  renderModal() {
    const { showModal, modalTab, existingLinks, existingLoading, newName, newUrl, creating, formError } = this.state;
    return (
      <Modal open={showModal} onClose={() => this.setState({ showModal: false })} title="Tag short link" width="440px">
        <div className={styles.modalHint}>
          This only tags a link for click tracking — it doesn't add anything to the message.
          Make sure this is the same short link you already put in the approved WhatsApp/DLT template.
        </div>
        <Tabs
          variant="pill"
          tabs={[{ key: 'existing', label: 'Existing' }, { key: 'new', label: 'New' }]}
          activeTab={modalTab}
          onChange={(key) => this.setState({ modalTab: key })}
        >
          {modalTab === 'existing' ? (
            <div className={styles.list}>
              {existingLoading ? (
                <Skeleton variant="rect" width="100%" height={120} style={{ borderRadius: 8 }} />
              ) : existingLinks.length === 0 ? (
                <EmptyState icon="🔗" title="No untagged short links" description="Create a new one instead, or tag one from the Attribution Links page first." />
              ) : (
                existingLinks.map((link) => (
                  <button key={link.id} className={styles.listItem} onClick={() => this.selectExisting(link)}>
                    <div className={styles.listItemName}>{link.name}</div>
                    <div className={styles.listItemUrl}>{REDIRECT_BASE_URL + link.short_code}</div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <div className={styles.form}>
              <Input label="Link name" required value={newName} onChange={(e) => this.setState({ newName: e.target.value })} placeholder="e.g. WhatsApp reminder — Sept" />
              <Input label="Destination URL" required value={newUrl} onChange={(e) => this.setState({ newUrl: e.target.value })} placeholder="https://pickmywork.com/some-page — must match the template" />
              {formError && <div className={styles.formErr}>{formError}</div>}
              <Button variant="primary" size="md" loading={creating} onClick={() => this.createAndTag()}>
                Create &amp; tag
              </Button>
            </div>
          )}
        </Tabs>
      </Modal>
    );
  }

  render() {
    const { attributionLinkId } = this.props;
    const { linkDetails, loadingDetails } = this.state;

    return (
      <div className={styles.wrap}>
        {!attributionLinkId ? (
          <button type="button" className={styles.insertBtn} onClick={() => this.openModal()}>
            <Icon name="add_link" size={14} /> Tag short link for click tracking
          </button>
        ) : loadingDetails || !linkDetails ? (
          <Skeleton variant="text" width={180} height={14} />
        ) : (
          <div className={styles.tag}>
            <Icon name="link" size={13} />
            <span className={styles.tagName}>{linkDetails.name}</span>
            <span className={styles.tagUrl}>{REDIRECT_BASE_URL + linkDetails.short_code}</span>
            {/* This exact message's own clicks — not the campaign/journey total,
                which can include other messages' tagged links too. */}
            <span className={styles.tagClicks} title="Clicks on this exact message's short link">
              <Icon name="ads_click" size={12} /> {linkDetails.click_count || 0}
            </span>
            <button type="button" className={styles.tagAction} onClick={() => this.openModal()} title="Change">
              <Icon name="edit" size={13} />
            </button>
            <button type="button" className={styles.tagAction} onClick={() => this.remove()} title="Remove">
              <Icon name="close" size={13} />
            </button>
          </div>
        )}
        {this.renderModal()}
      </div>
    );
  }
}

export default ShortLinkField;
